/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useRef } from "react";
import * as d3 from "d3";
import { Group } from "@iventis/types";
import { useObservableValue } from "@iventis/utilities";
import { debounceTime } from "rxjs/operators";
import { combineLatest } from "rxjs";
import { GroupAxisRow, GroupAxisRowHandle } from "./group-axis-row";
import { useGroupAxisScrollOffset, useUpdateGroupAxisHeight, useUpdateGroupScale, useViewportStream } from "./hooks";
import { CATEGORY_ROW_HEIGHT, TIME_AXIS_WIDTH } from "./calendar-context";
import { useSectionedGroups } from "./sectioned-groups-hook";
import { SectionedGroup } from "./types";

const CLIP_AXIS_PATH = "clipaxis";

/**
 * Given a group, this will generate a unique key concatonating it's id with its first subGroup recursively down the tree
 */
const createCategoryKey = (group: Group) => {
    let key = "";
    const addIdToKey = (_group: Group) => {
        key += _group.groupId || "";
        if (_group?.subGroups) {
            addIdToKey(_group.subGroups[0]);
        }
    };
    addIdToKey(group);
    return key;
};

export interface GroupScale {
    getGroupCoords(groupId: string): [number, number];
    getGroupAtCoord(value: number): Group | null;
}

export const GroupAxis: React.FC = React.memo(() => {
    const rowComponentsRef = useRef<GroupAxisRowHandle[]>([]);
    const containerRef = useRef<SVGGraphicsElement>();
    const groupAxisRef = useRef<SVGGraphicsElement>();
    const viewport$ = useViewportStream();
    const rowsRef = useRef<SVGGraphicsElement>();
    const categories$ = useSectionedGroups();
    const updateGroupAxisHeight = useUpdateGroupAxisHeight();
    const scrollOffset$ = useGroupAxisScrollOffset();
    const updateGroupScale = useUpdateGroupScale();
    const categories = useObservableValue(categories$);

    const updateGroupScaleHandle = useCallback(() => {
        updateGroupScale({
            getGroupCoords: (groupId: string): [number, number] => {
                // eslint-disable-next-line no-restricted-syntax
                for (const row of rowComponentsRef.current) {
                    const range = row.getRangeForGroup(groupId);
                    if (Array.isArray(range)) {
                        return [Math.floor(range[0]) + TIME_AXIS_WIDTH, Math.floor(range[1]) + TIME_AXIS_WIDTH];
                    }
                }
                return [0, 0];
            },
            getGroupAtCoord(value: number): Group | null {
                const rowComponent = rowComponentsRef.current[rowComponentsRef.current.length - 1];
                return rowComponent.getGroupAtCoord(value)?.group;
            },
        });
    }, [updateGroupScale]);
    // update group axis
    useEffect(() => {
        const sub = combineLatest([scrollOffset$]).subscribe(([scrollOffset]) => {
            // update grid axis content
            d3.select(containerRef.current).attr("transform", `translate(${TIME_AXIS_WIDTH}, 0)`);

            // scroll grid axis
            d3.select(groupAxisRef.current).attr("transform", `translate(${-scrollOffset},0)`);

            updateGroupScaleHandle();
        });

        return () => sub.unsubscribe();
    }, [viewport$, categories$, scrollOffset$]);

    // group axis height is used by the time axis. When categories list changes the height needs to be updated
    useEffect(() => {
        const sub = categories$.subscribe((categories) => {
            updateGroupAxisHeight(categories.length * CATEGORY_ROW_HEIGHT);
        });
        return () => sub.unsubscribe();
    }, [categories$, updateGroupAxisHeight]);

    // render single row of groups
    const groupAxisRowRenderer = useCallback(
        (categories: SectionedGroup[][]) =>
            (categories || []).map((groups, index) => (
                <GroupAxisRow
                    // The key changes if a new category is added so the UI can adjust to appending/removing sub groups
                    // IMPORTANT: Does not support changes within each category (i.e. append a new space inside a venue), updating the key is a temporary fix
                    key={createCategoryKey(groups[0].group)}
                    ref={(comp) => {
                        rowComponentsRef.current[index] = comp;
                    }}
                    groups={groups}
                    marginLeft={0}
                    marginTop={CATEGORY_ROW_HEIGHT * (index + 1)}
                    tickHeight={CATEGORY_ROW_HEIGHT}
                    showGrid={index === categories.length - 1}
                    isFirstRow={index === 0}
                />
            )),
        []
    );

    // update group scale handle. These functions are used for positioning sessions along the horizontal axis and to determine click coordinates
    // the update is delayed because position resolution depends on the rows
    useEffect(() => {
        const sub = combineLatest([categories$, scrollOffset$, viewport$])
            .pipe(debounceTime(50))
            .subscribe(() => {
                updateGroupScaleHandle();
            });

        return () => sub.unsubscribe();
    }, [categories$, scrollOffset$, updateGroupScale]);
    return (
        <g ref={groupAxisRef}>
            <g ref={containerRef} clipPath={`url(#${CLIP_AXIS_PATH})`}>
                <g ref={rowsRef}>{groupAxisRowRenderer(categories)}</g>
            </g>
        </g>
    );
});
